import { FC, useState, useMemo } from 'react';
import {
  Button,
  Card,
  DatePicker,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@features/ui';
import { TPADailyPLData } from '@src/types/reports';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import {
  dailyProfitOptions,
  paDailyPLAccountStatusOptions,
} from '@src/features/admin/reports/reportData';
import { getPAAccountDailyPnl } from '@src/services/reports';
import { format } from 'date-fns';

// Color palette for packages
const COLOR_PALETTE = [
  '#87CEEB',
  '#FFB6C1',
  '#FFD700',
  '#FFA500',
  '#20B2AA',
  '#9370DB',
  '#D3D3D3',
  '#FF6B6B',
  '#4ECDC4',
  '#95E1D3',
];

interface TooltipProps {
  active?: boolean;
  payload?: Array<{
    name: string;
    value: number;
    color: string;
  }>;
  label?: string;
  packageLabels: Record<string, string>;
}

const CustomTooltip: FC<TooltipProps> = ({
  active,
  payload,
  label,
  packageLabels,
}) => {
  if (active && payload && payload.length) {
    return (
      <div
        className="bg-card-stat p-3 rounded-lg border border-[#3D3D3D] flex flex-col gap-y-2"
        style={{
          boxShadow: '0px 7px 13px 0px #B6D72C14',
        }}
      >
        <p className="text-white font-semibold text-sm mb-1">{label}</p>
        {payload.map((entry, index) => (
          <div key={index} className="flex items-center gap-2">
            <div
              className="w-3 h-3 rounded-sm"
              style={{ backgroundColor: entry.color }}
            />
            <span className="text-[#999999] text-xs">
              {packageLabels[entry.name] || entry.name}:
            </span>
            <span className="text-white text-xs font-semibold ml-auto">
              {entry.value.toLocaleString()}
            </span>
          </div>
        ))}
        <div className="border-t border-[#3D3D3D] pt-2 mt-1">
          <div className="flex justify-between">
            <span className="text-[#999999] text-xs">Total:</span>
            <span className="text-[#B6D72C] text-sm font-semibold">
              {payload
                .reduce((sum, entry) => sum + entry.value, 0)
                .toLocaleString()}
            </span>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

const CustomLegend: FC<{
  packageLabels: Record<string, string>;
  packageColors: Record<string, string>;
}> = ({ packageLabels, packageColors }) => {
  return (
    <div className="flex flex-wrap gap-x-6 gap-y-2 justify-center mb-4 px-4">
      {Object.entries(packageLabels).map(([key, label]) => (
        <div key={key} className="flex items-center gap-2">
          <div
            className="w-4 h-4 rounded-sm"
            style={{
              backgroundColor: packageColors[key],
            }}
          />
          <span className="text-xs text-[#999999]">{label}</span>
        </div>
      ))}
    </div>
  );
};

// Helper function to generate a key from package name
const generatePackageKey = (packageName: string): string => {
  return `package_${packageName.toLowerCase().replace(/[^a-z0-9]+/g, '_')}`;
};

// Helper function to transform API response to chart data format
const transformApiDataToChartFormat = (
  apiData: TPADailyPLData[]
): {
  chartData: Record<string, any>[];
  packageLabels: Record<string, string>;
  packageColors: Record<string, string>;
} => {
  const dayKeys = [
    'days_3',
    'days_5',
    'days_7',
    'days_10',
    'days_15',
    'days_20',
    'days_25',
    'days_30',
  ] as const;

  // Build dynamic package mapping
  const packageLabels: Record<string, string> = {};
  const packageColors: Record<string, string> = {};
  const packageKeyMap: Record<number, string> = {};

  apiData.forEach((pkg, index) => {
    const key = generatePackageKey(pkg.package_name);

    packageKeyMap[pkg.package_id] = key;
    packageLabels[key] = pkg.package_name;
    packageColors[key] = COLOR_PALETTE[index % COLOR_PALETTE.length];
  });

  // Transform data from package-centric to day-centric
  const chartData = dayKeys.map((dayKey) => {
    const dayNumber = dayKey.replace('days_', '');
    const dataPoint: Record<string, any> = {
      days_profit: `${dayNumber}+`,
    };

    apiData.forEach((packageData) => {
      const chartKey = packageKeyMap[packageData.package_id];

      if (chartKey) {
        dataPoint[chartKey] = packageData[dayKey];
      }
    });

    return dataPoint;
  });

  return { chartData, packageLabels, packageColors };
};

export const PADailyPL: FC = () => {
  const [tradingStartDate, setTradingStartDate] = useState<Date | undefined>();
  const [dailyProfit, setDailyProfit] = useState<string>('200');
  const [accountStatus, setAccountStatus] = useState<string>('all');
  const [data, setData] = useState<Record<string, any>[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [packageLabels, setPackageLabels] = useState<Record<string, string>>(
    {}
  );
  const [packageColors, setPackageColors] = useState<Record<string, string>>(
    {}
  );
  const [reportInfo, setReportInfo] = useState<{
    title: string;
    generatedAt: string;
    totalPackages: number;
  }>({
    title: '',
    generatedAt: '',
    totalPackages: 0,
  });

  const handleGetData = async () => {
    setIsLoading(true);
    try {
      const params = {
        start_date: tradingStartDate
          ? format(tradingStartDate, 'yyyy-MM-dd')
          : undefined,
        live_accounts: accountStatus === 'live_only',
        profit: parseInt(dailyProfit),
      };

      const response = await getPAAccountDailyPnl(params);
      const { chartData, packageLabels, packageColors } =
        transformApiDataToChartFormat(response.data);

      setData(chartData);
      setPackageLabels(packageLabels);
      setPackageColors(packageColors);
      setReportInfo({
        title: response.title,
        generatedAt: response.generated_at,
        totalPackages: response.total_packages,
      });
    } catch (error) {
      console.error('Failed to fetch PA daily PNL data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Get package keys for rendering bars
  const packageKeys = useMemo(
    () => Object.keys(packageLabels),
    [packageLabels]
  );

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold text-white">PA Daily P/L</h1>

      <Card className="p-6">
        <div className="flex flex-col gap-4">
          <div className="flex items-end gap-4">
            <div className="flex flex-col gap-2 flex-1">
              <label className="text-sm text-[#999999]">
                Trading Start Date
              </label>
              <DatePicker
                className="h-[44px]"
                value={tradingStartDate}
                onChange={(date) => setTradingStartDate(date)}
              />
            </div>

            <div className="flex flex-col gap-2 flex-1">
              <label className="text-sm text-[#999999]">Daily Profit</label>
              <Select value={dailyProfit} onValueChange={setDailyProfit}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select daily profit" />
                </SelectTrigger>
                <SelectContent>
                  {dailyProfitOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex flex-col gap-2 flex-1">
              <label className="text-sm text-[#999999]">Account Status</label>
              <Select value={accountStatus} onValueChange={setAccountStatus}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select account status" />
                </SelectTrigger>
                <SelectContent>
                  {paDailyPLAccountStatusOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <Button
              className="bg-[#B6D72C] hover:bg-[#A5C128] text-black font-semibold px-8"
              disabled={isLoading}
              onClick={handleGetData}
            >
              {isLoading ? 'Loading...' : 'Run Report'}
            </Button>
          </div>

          {reportInfo.title && (
            <div className="text-sm text-[#999999]">
              <p>
                <span className="font-semibold">{reportInfo.title}</span> -{' '}
                Generated: {reportInfo.generatedAt} | Total Packages:{' '}
                {reportInfo.totalPackages}
              </p>
            </div>
          )}
        </div>
      </Card>

      <Card className="p-6">
        <div className="space-y-4">
          <CustomLegend
            packageColors={packageColors}
            packageLabels={packageLabels}
          />
          <ResponsiveContainer height={500} width="100%">
            <BarChart
              data={data}
              margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
            >
              <CartesianGrid stroke="#3D3D3D" strokeDasharray="3 3" />
              <XAxis
                axisLine={{ stroke: '#3D3D3D' }}
                dataKey="days_profit"
                label={{
                  value: 'DAYS PROFIT',
                  position: 'insideBottom',
                  offset: -5,
                  style: { fill: '#999999', fontSize: 12 },
                }}
                tick={{ fill: '#999999', fontSize: 12 }}
              />
              <YAxis
                axisLine={{ stroke: '#3D3D3D' }}
                label={{
                  value: 'NO. OF ACCOUNTS',
                  angle: -90,
                  position: 'insideLeft',
                  style: {
                    fill: '#999999',
                    fontSize: 12,
                    textAnchor: 'middle',
                  },
                }}
                tick={{ fill: '#999999', fontSize: 12 }}
              />
              <Tooltip
                content={<CustomTooltip packageLabels={packageLabels} />}
                cursor={{ fill: 'rgba(182, 215, 44, 0.1)' }}
              />

              {packageKeys.map((key) => (
                <Bar
                  key={key}
                  dataKey={key}
                  fill={packageColors[key]}
                  radius={[4, 4, 0, 0]}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>
    </div>
  );
};
